/**
 * Pure spell/weapon scoring and criteria-formatting helpers used by the
 * generator UI's criteria sheet and score computation. Extracted verbatim
 * from src/pages/Generator.js (see docs/generator-slicing.md, slice 1) -
 * no React, no component state, no side effects.
 */

import { calcSpellRows, calcWeaponCardRow, isEliotropeClass, calcLineExpectedVal, statsAtPosition } from './spellUtils';
import * as scoreCalc from './scoreCalculation';
import {
    spellLineCriterionSignature,
    spellWeightKey,
    WEAPON_WEIGHT_KEY,
} from './criteriaWeights';
import { weaponPoolReference } from './localSolve/weaponPoolReference';
import { bestWeaponValue } from './bestWeaponMetric';

const TXT_BEST_WEAPON = {
    fr: 'Meilleure arme',
    en: 'Best weapon',
    de: 'Beste Waffe',
    es: 'Mejor arma',
    pt: 'Melhor arma',
};

// Criterion identity lives in core/criteriaWeights.js (it is also the key a
// per-criterion weight is stored under); re-exported so existing call sites keep working.
export { spellLineCriterionSignature };

// PUSH_TERM_SCALE lives in core/scoreCalculation.js (score-calc constant, kept in
// sync with backend P.push_term_scale). Frontend models push lines as
// { kind: 'damage', el: 'push' } (see spellUtils
// pushLineFromCells / mapLine), unlike the backend SpellLine (kind === 'push').
export const isPushOnlyLines = (lines) =>
    Array.isArray(lines) && lines.length > 0 && lines.every((ln) => ln?.el === 'push');

export function lineMatchesCriterion(line, lineKeys) {
    if (!line || !Array.isArray(line?.sourceKeys) || !Array.isArray(lineKeys)) return false;
    if (!lineKeys.length) return false;
    const set = new Set(line.sourceKeys.map((x) => String(x || '').trim()).filter(Boolean));
    return lineKeys.some((k) => set.has(String(k || '').trim()));
}

export function collectPrimaryLinesRecursive(row, out = [], depth = 0) {
    if (!row || depth > 6) return out;
    out.push(
        ...(row.baseLines || []),
        ...((row.conditionalBlocks || []).flatMap((b) => b.lines || [])),
    );
    (row.summonChildRows || []).forEach((child) => collectPrimaryLinesRecursive(child, out, depth + 1));
    return out;
}

export function sumLinesExpectedWithXor(lines, critPct, stats = null) {
    const grouped = new Map();
    let sum = 0;
    (lines || []).forEach((line) => {
        if (!line) return;
        if (!['damage', 'heal', 'push'].includes(line.kind)) return;
        const ev = Number(calcLineExpectedVal(line, critPct, stats)) || 0;
        const xorKey = line.exclusiveGroupKey || line.elVariantXorKey;
        const xor = xorKey ? `xor:${xorKey}` : null;
        if (!xor) {
            sum += ev;
            return;
        }
        const prev = grouped.get(xor);
        if (prev == null || ev > prev) grouped.set(xor, ev);
    });
    grouped.forEach((v) => { sum += v; });
    return sum;
}

/** A spell criterion's weights-column multiplier; 1 when the build has none. */
function criterionImportance(build, criterion) {
    const key = spellWeightKey(criterion);
    const w = key && typeof build?.getCriterionWeight === 'function'
        ? Number(build.getCriterionWeight(key))
        : NaN;
    return Number.isFinite(w) ? w : 1;
}

/**
 * Card-display position of the line a criterion targets, so `#N` badges follow
 * the spell card's top-to-bottom order (base lines before conditional rows)
 * instead of an arbitrary line-key string sort. Returns +Infinity when the
 * line's position can't be resolved (falls back to signature order).
 */
export function spellCriterionCardOrder(criterion, spellId, lineOrderBySpell) {
    const orderList = lineOrderBySpell?.get?.(spellId);
    if (!Array.isArray(orderList)) return Number.POSITIVE_INFINITY;
    const keys = new Set(
        (criterion.line_keys ?? criterion.lineKeys ?? [])
            .map((x) => String(x || '').trim())
            .filter(Boolean)
    );
    if (!keys.size) return Number.POSITIVE_INFINITY;
    for (let i = 0; i < orderList.length; i += 1) {
        const src = orderList[i];
        if (Array.isArray(src) && src.some((k) => keys.has(String(k || '').trim()))) {
            return i;
        }
    }
    return Number.POSITIVE_INFINITY;
}

export function buildSpellLineIndicatorMap(criteria = [], lineOrderBySpell = null) {
    const groups = new Map();
    (Array.isArray(criteria) ? criteria : []).forEach((criterion) => {
        if (!criterion || typeof criterion !== 'object') return;
        const spellId = Number(criterion.spell_id ?? criterion.spellId ?? criterion.id);
        const sig = spellLineCriterionSignature(criterion);
        if (!Number.isFinite(spellId) || !sig) return;
        if (!groups.has(spellId)) groups.set(spellId, new Map());
        const bySig = groups.get(spellId);
        if (!bySig.has(sig)) {
            bySig.set(sig, spellCriterionCardOrder(criterion, spellId, lineOrderBySpell));
        }
    });
    const out = new Map();
    groups.forEach((bySig) => {
        if (bySig.size <= 1) return;
        const entries = Array.from(bySig, ([sig, order]) => ({ sig, order }));
        // Topmost card line = #1; unresolved positions sink to the bottom, with a
        // stable signature tiebreak so numbering is deterministic.
        entries.sort((a, b) => {
            const ao = Number.isFinite(a.order) ? a.order : Number.MAX_SAFE_INTEGER;
            const bo = Number.isFinite(b.order) ? b.order : Number.MAX_SAFE_INTEGER;
            return (ao - bo) || a.sig.localeCompare(b.sig);
        });
        entries.forEach((e, idx) => out.set(e.sig, `#${idx + 1}`));
    });
    return out;
}

export function isBackendEligiblePrimaryLine(line) {
    if (!line || !['damage', 'heal', 'push'].includes(line.kind)) return false;
    if (Array.isArray(line.conditions) && line.conditions.length > 0) {
        const ids = new Set(
            line.conditions
                .map((c) => c?.id ?? c?.Id)
                .filter((id) => id != null)
                .map((id) => String(id))
        );
        const allowed = new Set(['through_portal', 'direct']);
        for (const id of ids) {
            if (!allowed.has(id)) return false;
        }
    }
    return true;
}

export function computeSpellTermsForBuild({
    build,
    spellTypeId,
    spellScoringData,
    lvl,
    lang,
    position,
    useBoosts = false,
    includeDebug = false,
}) {
    const empty = { terms: [], importance: [], details: [], lineOrderBySpell: new Map(), spellCardOrderById: new Map() };
    if (!build || !spellTypeId) return empty;
    // The sheet the solver searches from, so the score shown beside a criterion
    // is the one it computed (Build.getSolverBoostEffects).
    const stats = statsAtPosition(build.getStats({ includePassiveEffects: true, solverView: true, useBoosts }), position);
    const nakedStats = Object.fromEntries(
        Object.entries(stats || {}).map(([k]) => [Number(k), { current: 0 }])
    );
    const clsName = build?.getCharacterClass?.()?.name || build?.characterClass?.name;
    const eliotropePortalCardOnly = isEliotropeClass(clsName);
    const rows = calcSpellRows({
        spells: [
            ...(Array.isArray(spellScoringData.spells) ? spellScoringData.spells : []),
            ...(Array.isArray(spellScoringData.other) ? spellScoringData.other : []),
        ],
        lvl,
        stats,
        lang,
        eliotropePortalCardOnly,
        summonCatalog: spellScoringData.summonCatalog || {},
        charges: build.spellCharges,
    });
    const baselineRows = calcSpellRows({
        spells: [
            ...(Array.isArray(spellScoringData.spells) ? spellScoringData.spells : []),
            ...(Array.isArray(spellScoringData.other) ? spellScoringData.other : []),
        ],
        lvl,
        stats: nakedStats,
        lang,
        eliotropePortalCardOnly,
        summonCatalog: spellScoringData.summonCatalog || {},
        charges: build.spellCharges,
    });
    const rowById = new Map(rows.map((r) => [Number(r.id), r]));
    const baselineById = new Map(baselineRows.map((r) => [Number(r.id), r]));
    // Spell-tab display order (calcSpellRows already groups rows by element):
    // spellId -> row index, so criteria chips can be grouped like the spell tab.
    const spellCardOrderById = new Map();
    rows.forEach((r, i) => {
        const id = Number(r?.id);
        if (Number.isFinite(id) && !spellCardOrderById.has(id)) spellCardOrderById.set(id, i);
    });
    const criteria = typeof build.getMaximizeSpellCriteria === 'function'
        ? build.getMaximizeSpellCriteria()
        : [];
    const terms = [];
    const importance = [];
    const details = [];
    // Per-spell ordered primary-line source keys (card top-to-bottom order),
    // used to number same-spell criteria (#1/#2) by their card position.
    const lineOrderBySpell = new Map();
    (Array.isArray(criteria) ? criteria : []).forEach((criterion) => {
        const spellId = Number(
            typeof criterion === 'number'
                ? criterion
                : (criterion?.spell_id ?? criterion?.spellId ?? criterion?.id)
        );
        if (!Number.isFinite(spellId)) return;
        const row = rowById.get(spellId);
        const baseRow = baselineById.get(spellId);
        if (!row || !baseRow) {
            details.push({
                spellId,
                skipped: true,
                reason: !row ? 'missing-row' : 'missing-baseline-row',
            });
            return;
        }
        const lineKeys = Array.from(new Set(
            ((criterion && typeof criterion === 'object')
                ? (criterion.line_keys ?? criterion.lineKeys ?? [])
                : [])
                .map((x) => String(x || '').trim())
                .filter(Boolean)
        ));
        const allLines = collectPrimaryLinesRecursive(row).filter(isBackendEligiblePrimaryLine);
        const allBaselineLines = collectPrimaryLinesRecursive(baseRow).filter(isBackendEligiblePrimaryLine);
        if (!lineOrderBySpell.has(spellId)) {
            lineOrderBySpell.set(
                spellId,
                allLines.map((ln) => (Array.isArray(ln?.sourceKeys) ? ln.sourceKeys : [])),
            );
        }
        let primary = 0;
        let baseline = 0;
        let matchedLines = [];
        let matchedBaselineLines = [];
        if (lineKeys.length > 0) {
            matchedLines = allLines.filter((ln) => lineMatchesCriterion(ln, lineKeys));
            matchedBaselineLines = allBaselineLines.filter((ln) => lineMatchesCriterion(ln, lineKeys));
            primary = sumLinesExpectedWithXor(matchedLines, row.critPct ?? 0, stats);
            baseline = sumLinesExpectedWithXor(matchedBaselineLines, baseRow.critPct ?? 0, nakedStats);
        } else {
            matchedLines = allLines;
            matchedBaselineLines = allBaselineLines;
            primary = sumLinesExpectedWithXor(matchedLines, row.critPct ?? 0, stats);
            baseline = sumLinesExpectedWithXor(matchedBaselineLines, baseRow.critPct ?? 0, nakedStats);
        }
        if (!(primary > 0 || baseline > 0)) {
            details.push({
                spellId,
                skipped: true,
                reason: 'zero-primary-and-baseline',
                lineKeys,
                allEligibleLineCount: allLines.length,
                matchedLineCount: matchedLines.length,
                allEligibleBaselineLineCount: allBaselineLines.length,
                matchedBaselineLineCount: matchedBaselineLines.length,
            });
            return;
        }
        const pushScale = isPushOnlyLines(matchedLines) ? scoreCalc.PUSH_TERM_SCALE : 1.0;
        const term = scoreCalc.computeSpellTermValue(primary, baseline, row?.apCost, pushScale);
        if (Number.isFinite(term)) {
            terms.push(term);
            // Positionally matched to `terms` — computeScore zips them.
            importance.push(criterionImportance(build, criterion));
        }
        // Light details always (score tooltip). Heavy line dumps only when debugging.
        const detail = {
            spellId,
            name: row?.name,
            lineKeys,
            critPct: row.critPct ?? 0,
            baselineCritPct: baseRow.critPct ?? 0,
            primary,
            baseline,
            term,
            allEligibleLineCount: allLines.length,
            matchedLineCount: matchedLines.length,
            allEligibleBaselineLineCount: allBaselineLines.length,
            matchedBaselineLineCount: matchedBaselineLines.length,
        };
        if (includeDebug) {
            const lineDebug = (lines, critPct, statMap) => lines.map((ln) => ({
                kind: ln?.kind,
                el: ln?.el,
                effectId: ln?.effectId,
                base: ln?.base,
                crit: ln?.crit,
                baseRaw: ln?.baseRaw,
                critRaw: ln?.critRaw,
                sourceKeys: ln?.sourceKeys,
                exclusiveGroupKey: ln?.exclusiveGroupKey,
                conditions: ln?.conditions,
                stackIndex: ln?.stackIndex,
                stackMax: ln?.stackMax,
                ev: calcLineExpectedVal(ln, critPct, statMap),
            }));
            detail.matchedLines = lineDebug(matchedLines, row.critPct ?? 0, stats);
            detail.matchedBaselineLines = lineDebug(matchedBaselineLines, baseRow.critPct ?? 0, nakedStats);
        }
        details.push(detail);
    });
    return { terms, importance, details, lineOrderBySpell, spellCardOrderById };
}

export function computeWeaponTermForBuild({
    build,
    lvl,
    lang,
    position,
    useBoosts = false,
    includeDebug = false,
}) {
    const empty = { terms: [], importance: [], details: [] };
    const bestWeapon = build?.maximizeBestWeapon === true;
    if ((!build?.maximizeWeapon && !bestWeapon) || typeof build?.getWeapon !== 'function') {
        return empty;
    }
    // Find-the-best-weapon measures the equipped weapon against the pool rather
    // than against itself, and that scale only exists once a generation has
    // fetched the pool. Until then the criterion is left out of the displayed
    // score: visibly absent beats silently measured on a different scale.
    const metric = bestWeapon && typeof build?.getBestWeaponMetric === 'function'
        ? build.getBestWeaponMetric()
        : null;
    const scale = bestWeapon ? weaponPoolReference(lvl, metric) : null;
    if (bestWeapon && !scale) return empty;
    // The sheet the solver searches from, so the score shown beside a criterion
    // is the one it computed (Build.getSolverBoostEffects).
    const stats = statsAtPosition(build.getStats({ includePassiveEffects: true, solverView: true, useBoosts }), position);
    const nakedStats = Object.fromEntries(
        Object.entries(stats || {}).map(([k]) => [Number(k), { current: 0 }])
    );
    const weaponRow = calcWeaponCardRow({
        weaponItem: build.getWeapon(),
        stats,
        lvl,
        lang,
        weaponMaging: build?.getWeaponMaging?.() ?? null,
    });
    const baselineRow = calcWeaponCardRow({
        weaponItem: build.getWeapon(),
        stats: nakedStats,
        lvl,
        lang,
        weaponMaging: build?.getWeaponMaging?.() ?? null,
    });
    if (!weaponRow || !baselineRow) {
        return {
            terms: [],
            importance: [],
            details: [{
                weaponId: build.getWeapon()?.id ?? null,
                isWeapon: true,
                skipped: true,
                reason: !weaponRow ? 'missing-weapon-row' : 'missing-weapon-baseline-row',
            }],
        };
    }
    const primary = Number(weaponRow.cardPrimaryTotal) || 0;
    const baseline = Number(baselineRow.cardPrimaryTotal) || 0;
    if (!(primary > 0 || baseline > 0)) {
        return {
            terms: [],
            importance: [],
            details: [{
                weaponId: build.getWeapon()?.id ?? null,
                name: weaponRow?.name,
                isWeapon: true,
                skipped: true,
                reason: 'zero-primary-and-baseline',
                primary,
                baseline,
            }],
        };
    }
    // Find-the-best-weapon measures the weapon against the pool on the metric
    // it ranks on, so it has its own term — the equipped-weapon criterion still
    // measures a weapon against itself.
    const term = bestWeapon
        ? scoreCalc.computeBestWeaponTermValue(
            bestWeaponValue(weaponRow, metric),
            scale.reference,
            scale.referenceDpa,
        )
        : scoreCalc.computeSpellTermValue(primary, baseline, weaponRow?.apCost, 1.0);
    const details = Number.isFinite(term) ? [{
        weaponId: build.getWeapon()?.id ?? null,
        // Named for the criterion, not just the weapon: in the score tooltip
        // this row sits among the spells, where "Sceptre du Minotot" alone
        // would read as the pinned-weapon criterion — the opposite request.
        name: bestWeapon
            ? `${TXT_BEST_WEAPON[lang] || TXT_BEST_WEAPON.en}${weaponRow?.name ? ` · ${weaponRow.name}` : ''}`
            : weaponRow?.name,
        isWeapon: true,
        isBestWeapon: bestWeapon,
        primary,
        baseline,
        term,
        ...(includeDebug ? { apCost: weaponRow?.apCost } : {}),
    }] : [];
    const weaponImportance = typeof build?.getCriterionWeight === 'function'
        ? Number(build.getCriterionWeight(WEAPON_WEIGHT_KEY)) || 1
        : 1;
    return {
        terms: Number.isFinite(term) ? [term] : [],
        importance: Number.isFinite(term) ? [weaponImportance] : [],
        details,
    };
}

/**
 * Singular/plural "active criteria" wording per language, plus the label
 * separator. `pluralFrom` is the smallest count taking the plural form: French
 * keeps the singular at zero ("0 critère actif"), the others do not.
 */
const CRITERIA_TOOLTIP = {
    fr: { sep: ' : ', one: 'critère actif', many: 'critères actifs', pluralFrom: 2 },
    en: { sep: ': ', one: 'active criterion', many: 'active criteria', pluralFrom: 0 },
    de: { sep: ': ', one: 'aktives Kriterium', many: 'aktive Kriterien', pluralFrom: 0 },
    es: { sep: ': ', one: 'criterio activo', many: 'criterios activos', pluralFrom: 0 },
    pt: { sep: ': ', one: 'critério ativo', many: 'critérios ativos', pluralFrom: 0 },
};

/** Data tab tooltip: e.g. "Stats : 3 critères actifs" / "Stats: 3 active criteria" */
export function formatDataTabCriteriaTooltip(tabLabel, count, lang) {
    const n = Math.max(0, Math.floor(Number(count)) || 0);
    const txt = CRITERIA_TOOLTIP[lang] || CRITERIA_TOOLTIP.en;
    const plural = n === 1 ? false : n >= txt.pluralFrom;
    return `${tabLabel}${txt.sep}${n} ${plural ? txt.many : txt.one}`;
}

export function localizedName(value, lang, fallback = '') {
    if (!value) return fallback;
    // English before French: a de/es/pt reader served the French name reads as a
    // bug, whereas English is the app's documented fallback for missing copy.
    if (typeof value === 'object') return value[lang] || value.en || value.fr || fallback;
    return String(value);
}

export function capitalizeCriterionStatLabel(value) {
    const text = String(value || '');
    if (!text) return text;
    return text
        .replace(/([a-zà-ÿ])\./gi, (_, ch) => `${ch.toLocaleUpperCase()}.`)
        .replace(/(^|[\s/])([a-zà-ÿ])/gi, (_, prefix, ch) => `${prefix}${ch.toLocaleUpperCase()}`);
}

export function formatStatCriterionLabel(statDef, rule, lang) {
    const label = capitalizeCriterionStatLabel(statDef?.alias?.[lang] || statDef?.label?.[lang] || statDef?.label?.en || String(statDef?.id || ''));
    if (rule?.maximize) return label;
    const min = Number(rule?.min);
    return Number.isFinite(min) ? `${label} ≥ ${Math.round(min)}` : label;
}
